import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Column, Columns, EmptyState } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useCallback, useState } from 'react';
import { analytics } from 'app/utils/segment';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import styles from '../../../searchResults.module.scss';
import { useSearchSplitLayout } from '../../../utils/useSearchSplitLayout';
import { SearchResultsEmpty } from '../../empty/searchResultsEmpty';
import { SearchResultsPagination } from '../../pagination/searchResultsPagination';
import { SearchResultsSort } from '../../sort/searchResultsSort';
import { ConversationPreview } from '../conversations/components/preview/conversationPreview';
import { NoteResult } from './components/result/noteResult';
/**
 * Represents a component that is used to render the notes tab of the search
 * results
 */
export const NotesResults = ({ result }) => {
    var _a, _b, _c, _d, _e, _f;
    const navigate = useAppNavigate();
    const [previewedHit, setPreviewedHit] = useState(null);
    const { leftColumnSize, rightColumnSize } = useSearchSplitLayout({
        isPreviewing: !!previewedHit,
    });
    const handleNavigateToConversation = useCallback(() => {
        if (!previewedHit)
            return;
        analytics.searchNotePreviewGoToClicked();
        navigate({
            path: '/conversation/:csid',
            params: {
                path: {
                    csid: previewedHit === null || previewedHit === void 0 ? void 0 : previewedHit.numeric_id.toString(),
                },
            },
        });
    }, [navigate, previewedHit]);
    const handleRowClick = useCallback((hit) => {
        if (hit.numeric_id === (previewedHit === null || previewedHit === void 0 ? void 0 : previewedHit.numeric_id))
            return;
        setPreviewedHit(hit);
        analytics.searchNotePreviewOpened();
    }, [previewedHit, setPreviewedHit]);
    const handlePreviewClose = useCallback(() => {
        setPreviewedHit(null);
        analytics.searchNotePreviewClosed();
    }, [setPreviewedHit]);
    const hitsCount = (_a = result === null || result === void 0 ? void 0 : result.hits.length) !== null && _a !== void 0 ? _a : 0;
    if (hitsCount === 0) {
        return _jsx(SearchResultsEmpty, {});
    }
    /**
     * `ConversationPreview` component expects _inner_hits, but for notes the API
     * returns _highlights. This conversion is done so we can use that component
     * for both conversations and notes.
     */
    const highlightsToInnerHits = ((_c = (_b = previewedHit === null || previewedHit === void 0 ? void 0 : previewedHit._highlights) === null || _b === void 0 ? void 0 : _b.message) === null || _c === void 0 ? void 0 : _c.length)
        ? [
            {
                id: previewedHit.id,
                highlight: {
                    text: (_d = previewedHit._highlights.message[0]) !== null && _d !== void 0 ? _d : '',
                },
            },
        ]
        : [];
    return (_jsxs(Columns, { children: [_jsxs(Column, Object.assign({ size: leftColumnSize }, { children: [_jsx(Column.Row, Object.assign({ slottable: true, borderBottom: true, noMinHeight: true }, { children: _jsx(Column.Slot, Object.assign({ placement: "end" }, { children: _jsx(SearchResultsSort, {}) })) })), _jsx(Column.Row, Object.assign({ scrollable: true, noVerticalPadding: true, noHorizontalPadding: true }, { children: result === null || result === void 0 ? void 0 : result.hits.map((hit, index) => {
                            return (_jsx(NoteResult, { className: classnames(styles.rowClickable, {
                                    [styles.rowSelected]: hit.id === (previewedHit === null || previewedHit === void 0 ? void 0 : previewedHit.id),
                                }), hit: hit, onRowClick: handleRowClick }, `${hit.id}-${index}`));
                        }) })), _jsx(Column.Row, Object.assign({ noVerticalPadding: true, noHorizontalPadding: true, borderTop: true }, { children: _jsx(SearchResultsPagination, { pageCount: (_e = result === null || result === void 0 ? void 0 : result.nb_pages) !== null && _e !== void 0 ? _e : 1, currentPage: (_f = result === null || result === void 0 ? void 0 : result.page) !== null && _f !== void 0 ? _f : 0 }) }))] })), _jsx(Column, Object.assign({ size: rightColumnSize }, { children: _jsx(Column.Row, Object.assign({ scrollable: true, noVerticalPadding: true, noHorizontalPadding: true }, { children: previewedHit ? (_jsx(ConversationPreview, { numericId: previewedHit.numeric_id, highlights: previewedHit._highlights, innerHits: highlightsToInnerHits, onGoToConversation: handleNavigateToConversation, onPreviewClose: handlePreviewClose })) : (_jsx(Box, Object.assign({ padding: "500 0" }, { children: _jsx(EmptyState, { title: "Select a note", description: _jsxs("span", { children: ["Want to see a preview of the note you're looking for?", ' ', _jsx("br", {}), " Go ahead and click a note to see it here."] }) }) }))) })) }))] }));
};
